import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import HorseCard from '../components/TheHerd/HorseCard';
import AddEditHorseDialog from '../components/TheHerd/dialogs/AddEditHorseDialog';
import { useHorseData } from '../components/TheHerd/hooks/useHorseData';
import { showRewardNotification } from '../components/TheHerd/shared/RewardNotification';
import { useHerdSettings } from '../hooks/useHerdSettings';
import { api } from '../App';
import {
  calculateBondingIncrease,
  calculateBondingDecay,
  getTodayDateString,
  shouldResetDailyCare,
  createEmptyDailyCareChecklist,
  hasMetMinimumDailyCare,
  countCompletedDailyTasks
} from '../utils/horseHelpers';
import { THEME_COLORS, BONDING_CONFIG } from '../constants/horseConstants';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Lock, CreditCard, Check } from 'lucide-react';
import { toast } from 'sonner';

const TheHerd = ({ user, setUser, logout }) => {
  const {
    mustangs,
    loading,
    fetchMustangs,
    addHorse,
    updateHorse,
    deleteHorse,
    optimisticUpdate,
    setMustangs
  } = useHorseData(api);

  // Fetch customizable herd settings (daily care and training categories)
  const { dailyCareCategories, trainingCategories, loading: settingsLoading } = useHerdSettings();

  const [showAddHorseDialog, setShowAddHorseDialog] = useState(false);
  const [editingHorse, setEditingHorse] = useState(null);
  
  // Subscription state
  const [hasSubscription, setHasSubscription] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  
  // Check if user is admin (can bypass subscription)
  const isAdmin = user?.is_admin;
  
  // Check subscription on mount
  useEffect(() => {
    checkSubscription();
  }, []);
  
  const checkSubscription = async () => {
    try {
      // Check for Virtual Corral subscription (includes The Herd access)
      const vcResponse = await api.get('/virtual-corral/subscription');
      if (vcResponse.data.has_subscription) {
        setHasSubscription(true);
        setCheckingSubscription(false);
        return;
      }
      
      // Check for standalone Herd membership
      const herdResponse = await api.get('/subscriptions/active');
      const hasHerdMembership = herdResponse.data?.subscriptions?.some(
        sub => sub.plan_type === 'herd_membership' && sub.is_active
      );
      
      setHasSubscription(hasHerdMembership);
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasSubscription(false);
    } finally {
      setCheckingSubscription(false);
    }
  };

  /**
   * Handle gentling checkbox changes with bonding level updates
   */
  const handleGentlingCheckboxChange = async (mustang, category, itemKey, checked) => {
    try {
      const updatedGentling = {
        ...mustang.gentling,
        [category]: {
          ...mustang.gentling?.[category],
          [itemKey]: checked
        }
      };

      // Optimistically update UI
      optimisticUpdate(mustang.id, { gentling: updatedGentling });

      if (mustang.id.startsWith('local_')) {
        // Update local horse in localStorage
        const localHorses = JSON.parse(localStorage.getItem('localHorses') || '[]');
        const updatedLocalHorses = localHorses.map(h =>
          h.id === mustang.id ? { ...h, gentling: updatedGentling } : h
        );
        localStorage.setItem('localHorses', JSON.stringify(updatedLocalHorses));
      } else {
        // Handle API horses normally
        const response = await api.put(`/user-mustangs/${mustang.id}`, { gentling: updatedGentling });

        // Show reward notification if coins or XP were earned
        if (response.data.rewards && checked) {
          const { coins, xp } = response.data.rewards;
          if (coins > 0 || xp > 0) {
            showRewardNotification(coins, xp);
          }
        }
      }
    } catch (error) {
      console.error('Error updating gentling:', error);
      // Revert on error
      await fetchMustangs();
      alert('Failed to update. Please try again.');
    }
  };

  /**
   * Handle daily care checkbox changes with bonding level updates
   */
  const handleDailyCareCheckboxChange = async (mustang, category, subItem, checked) => {
    try {
      const today = getTodayDateString();
      const oldDate = mustang.daily_care_date;

      // Check if we need to reset (new day)
      const needsReset = shouldResetDailyCare(oldDate);

      let updatedDaily;
      if (needsReset) {
        // Reset all to false structure, then set the clicked one
        updatedDaily = createEmptyDailyCareChecklist();
        updatedDaily[category][subItem] = checked;
      } else {
        // Normal update - nested structure
        updatedDaily = {
          ...mustang.daily_care_checklist,
          [category]: {
            ...mustang.daily_care_checklist?.[category],
            [subItem]: checked
          }
        };
      }

      // Calculate bonding level change
      let newBondingLevel = mustang.bonding_level || 0;
      let bondingPointsEarned = 0;

      if (needsReset) {
        // Apply decay first (for missed days)
        newBondingLevel = calculateBondingDecay(newBondingLevel, oldDate);
      }

      if (checked) {
        // Increase bonding for completed task
        const oldLevel = newBondingLevel;
        newBondingLevel = calculateBondingIncrease(newBondingLevel, 1);
        bondingPointsEarned = newBondingLevel - oldLevel;
      }

      // Check if minimum daily care is met to prevent decay tomorrow
      const meetsMinimum = hasMetMinimumDailyCare(updatedDaily);

      // Optimistically update UI
      optimisticUpdate(mustang.id, {
        daily_care_checklist: updatedDaily,
        daily_care_date: today,
        bonding_level: newBondingLevel,
        last_care_date: meetsMinimum ? today : mustang.last_care_date
      });

      if (mustang.id.startsWith('local_')) {
        // Update local horse in localStorage
        const localHorses = JSON.parse(localStorage.getItem('localHorses') || '[]');
        const updatedLocalHorses = localHorses.map(h =>
          h.id === mustang.id
            ? {
                ...h,
                daily_care_checklist: updatedDaily,
                daily_care_date: today,
                bonding_level: newBondingLevel,
                last_care_date: meetsMinimum ? today : h.last_care_date
              }
            : h
        );
        localStorage.setItem('localHorses', JSON.stringify(updatedLocalHorses));
      } else {
        // Handle API horses normally
        const response = await api.put(`/user-mustangs/${mustang.id}`, {
          daily_care_checklist: updatedDaily,
          daily_care_date: today,
          bonding_level: newBondingLevel,
          last_care_date: meetsMinimum ? today : mustang.last_care_date
        });

        // Show reward notification
        if (checked) {
          const rewards = response.data.rewards || {};
          const coins = rewards.coins || 0;
          const xp = rewards.xp || 0;

          if (coins > 0 || xp > 0 || bondingPointsEarned > 0) {
            showRewardNotification(coins, xp, bondingPointsEarned);
          }
        }
      }
    } catch (error) {
      console.error('Error updating daily care checklist:', error);
      // Revert on error
      await fetchMustangs();
      alert('Failed to update. Please try again.');
    }
  };

  /**
   * Handle adding a new horse
   */
  const handleAddHorse = async () => {
    const result = await addHorse();
    if (result.success) {
      alert(result.message);
    } else {
      alert(result.message);
    }
  };

  /**
   * Handle editing a horse
   */
  const handleEditHorse = (mustang) => {
    setEditingHorse(mustang);
    setShowAddHorseDialog(true);
  };

  /**
   * Handle saving horse changes
   */
  const handleSaveHorse = async (horseData) => {
    try {
      if (editingHorse) {
        // Editing existing horse
        const result = await updateHorse(editingHorse.id, horseData);
        if (!result.success) {
          alert('Failed to update horse');
        }
      } else {
        // Adding new horse (shouldn't reach here with current flow)
        console.log('Adding new horse via dialog not implemented');
      }

      setShowAddHorseDialog(false);
      setEditingHorse(null);
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save horse');
    }
  };

  /**
   * Handle deleting a horse
   */
  const handleDeleteHorse = async (mustangId, mustangName) => {
    if (!window.confirm(`Are you sure you want to remove ${mustangName} from your herd?`)) {
      return;
    }

    const result = await deleteHorse(mustangId);
    if (result.success) {
      alert(`${mustangName} has been removed from your herd`);
    } else {
      alert('Failed to delete horse');
    }
  };

  /**
   * Handle sprite change from Interactive card
   */
  const handleSpriteChange = async (mustangId, spriteId) => {
    try {
      // Optimistically update UI
      optimisticUpdate(mustangId, { selected_sprite: spriteId });

      // Save to backend
      await api.put(`/user-mustangs/${mustangId}`, { selected_sprite: spriteId });
      toast.success('Sprite updated!');
    } catch (error) {
      console.error('Error updating sprite:', error);
      toast.error('Failed to update sprite');
      // Revert on error
      await fetchMustangs();
    }
  };

  /**
   * Handle feed action from Interactive card - costs 10 coins
   */
  const handleFeed = async (mustangId) => {
    try {
      const response = await api.post(`/interactive/feed/${mustangId}`);
      const { hunger_level, bonding_level, coins_spent, coins_earned, new_balance, xp_earned, total_xp, ranch_level } = response.data;
      
      // Update mustang state including bonding
      optimisticUpdate(mustangId, { hunger_level, bonding_level });
      
      // Update user's coin balance and XP immediately in UI
      if (setUser) {
        setUser(prev => ({ ...prev, ranch_coins: new_balance, ranch_xp: total_xp, ranch_level }));
      }
      
      toast.success(`Fed your horse! -${coins_spent} coins, +${coins_earned} coins, +${xp_earned} XP`);
    } catch (error) {
      console.error('Error feeding horse:', error);
      const message = error.response?.data?.detail || 'Failed to feed horse';
      toast.error(message);
    }
  };

  /**
   * Handle water action from Interactive card - costs 10 coins
   */
  const handleWater = async (mustangId) => {
    try {
      const response = await api.post(`/interactive/water/${mustangId}`);
      const { hydration_level, bonding_level, coins_spent, coins_earned, new_balance, xp_earned, total_xp, ranch_level } = response.data;
      
      // Update mustang state including bonding
      optimisticUpdate(mustangId, { hydration_level, bonding_level });
      
      // Update user's coin balance and XP immediately in UI
      if (setUser) {
        setUser(prev => ({ ...prev, ranch_coins: new_balance, ranch_xp: total_xp, ranch_level }));
      }
      
      toast.success(`Watered your horse! -${coins_spent} coins, +${coins_earned} coins, +${xp_earned} XP`);
    } catch (error) {
      console.error('Error watering horse:', error);
      const message = error.response?.data?.detail || 'Failed to water horse';
      toast.error(message);
    }
  };

  /**
   * Handle groom action from Interactive card - costs 10 coins
   */
  const handleGroom = async (mustangId) => {
    try {
      const response = await api.post(`/interactive/groom/${mustangId}`);
      const { cleanliness_level, bonding_level, coins_spent, coins_earned, new_balance, xp_earned, total_xp, ranch_level } = response.data;
      
      // Update mustang state including bonding
      optimisticUpdate(mustangId, { cleanliness_level, bonding_level });
      
      // Update user's coin balance and XP immediately in UI
      if (setUser) {
        setUser(prev => ({ ...prev, ranch_coins: new_balance, ranch_xp: total_xp, ranch_level }));
      }
      
      toast.success(`Groomed your horse! -${coins_spent} coins, +${coins_earned} coins, +${xp_earned} XP`);
    } catch (error) {
      console.error('Error grooming horse:', error);
      const message = error.response?.data?.detail || 'Failed to groom horse';
      toast.error(message);
    }
  };

  /**
   * Handle exercise action from Interactive card - costs 10 coins
   */
  const handleExercise = async (mustangId) => {
    try {
      const response = await api.post(`/interactive/exercise/${mustangId}`);
      const { fitness_level, bonding_level, coins_spent, coins_earned, new_balance, xp_earned, total_xp, ranch_level } = response.data;
      
      // Update mustang state including bonding
      optimisticUpdate(mustangId, { fitness_level, bonding_level });
      
      // Update user's coin balance and XP immediately in UI
      if (setUser) {
        setUser(prev => ({ ...prev, ranch_coins: new_balance, ranch_xp: total_xp, ranch_level }));
      }
      
      toast.success(`Exercised your horse! -${coins_spent} coins, +${coins_earned} coins, +${xp_earned} XP`);
    } catch (error) {
      console.error('Error exercising horse:', error);
      const message = error.response?.data?.detail || 'Failed to exercise horse';
      toast.error(message);
    }
  };

  // Show loading while checking subscription
  if (checkingSubscription) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: THEME_COLORS.background }}>
        <Navigation user={user} logout={logout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: THEME_COLORS.primary, borderTopColor: 'transparent' }}></div>
            <p className="mt-4 text-lg" style={{ color: THEME_COLORS.primary }}>
              Checking subscription...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Subscription required screen (skip for admins)
  if (!hasSubscription && !isAdmin) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: THEME_COLORS.background }}>
        <Navigation user={user} logout={logout} />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
          <div className="text-center mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style={{backgroundColor: THEME_COLORS.primary}}>
              <Lock className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4" style={{color: THEME_COLORS.primary}}>
              Choose Your Subscription
            </h1>
            <p className="text-base sm:text-lg text-gray-600">
              Select the plan that works best for you
            </p>
          </div>

          {/* Two-tier subscription options */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* The Herd Only - $4.95/month */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-gray-200 hover:border-teal-400 transition-colors">
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold mb-2" style={{color: THEME_COLORS.secondary}}>The Herd Only</h2>
                <div className="text-4xl font-bold" style={{color: THEME_COLORS.secondary}}>
                  $4.95
                  <span className="text-lg text-gray-600">/month</span>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                {[
                  'Manage your horses in The Herd',
                  'Daily care tracking & checklists',
                  'Training progress milestones',
                  'Bonding level system',
                  'Add horses manually'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={async () => {
                  setProcessingPayment(true);
                  try {
                    const originUrl = window.location.protocol === 'https:' 
                      ? window.location.origin 
                      : `https://${window.location.host}`;
                    
                    const response = await api.post('/subscriptions/create-checkout', {
                      plan_type: 'herd_membership', 
                      origin_url: originUrl
                    });
                    
                    window.location.href = response.data.url;
                  } catch (error) {
                    console.error('Payment error:', error);
                    toast.error('Payment failed. Please try again.');
                    setProcessingPayment(false);
                  }
                }}
                className="w-full py-4 text-lg font-bold rounded-xl"
                style={{backgroundColor: THEME_COLORS.secondary, color: 'white'}}
                disabled={processingPayment}
              >
                {processingPayment ? 'Processing...' : 'Subscribe - $4.95/mo'}
              </Button>
            </div>

            {/* Virtual Corral + The Herd - $19.95/month */}
            <div className="bg-white rounded-2xl shadow-xl p-6 border-3 relative" style={{border: `3px solid ${THEME_COLORS.primary}`}}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-white text-sm font-bold" style={{backgroundColor: THEME_COLORS.primary}}>
                BEST VALUE
              </div>
              
              <div className="text-center mb-6 mt-2">
                <h2 className="text-xl font-bold mb-2" style={{color: THEME_COLORS.primary}}>Virtual Corral + The Herd</h2>
                <div className="text-4xl font-bold" style={{color: THEME_COLORS.primary}}>
                  $19.95
                  <span className="text-lg text-gray-600">/month</span>
                </div>
              </div>
              
              <ul className="space-y-3 mb-6">
                {[
                  'Everything in The Herd Only, PLUS:',
                  'Virtual Corral adoption experience',
                  'Choose adoption method (OLC, Satellite, Holding)',
                  'Adopt up to 4 virtual mustangs per event',
                  'Educational gaming experience',
                  'Earn achievements and badges'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className={idx === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}>{feature}</span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={async () => {
                  setProcessingPayment(true);
                  try {
                    const originUrl = window.location.protocol === 'https:' 
                      ? window.location.origin 
                      : `https://${window.location.host}`;
                    
                    const response = await api.post('/subscriptions/create-checkout', {
                      plan_type: 'monthly_unlimited', 
                      origin_url: originUrl
                    });
                    
                    window.location.href = response.data.url;
                  } catch (error) {
                    console.error('Payment error:', error);
                    toast.error('Payment failed. Please try again.');
                    setProcessingPayment(false);
                  }
                }}
                className="w-full py-4 text-lg font-bold rounded-xl"
                style={{backgroundColor: THEME_COLORS.primary, color: 'white'}}
                disabled={processingPayment}
              >
                {processingPayment ? 'Processing...' : 'Subscribe - $19.95/mo'}
              </Button>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500">
            All subscriptions auto-renew monthly. Cancel anytime.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: THEME_COLORS.background }}>
        <Navigation user={user} logout={logout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🐴</div>
            <p className="text-xl" style={{ color: THEME_COLORS.primary }}>
              Loading your herd...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME_COLORS.background }}>
      <Navigation user={user} logout={logout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ color: THEME_COLORS.primary }}>
          The Herd
        </h1>

        {/* My Horses List */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: THEME_COLORS.primary }}>
              My Horses
            </h2>
            <button
              onClick={handleAddHorse}
              style={{
                backgroundColor: THEME_COLORS.secondary,
                color: 'white',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              ➕ Add Horse
            </button>
          </div>

          {mustangs.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-8xl mb-4">🐴</div>
              <p className="text-xl mb-4" style={{ color: THEME_COLORS.primary }}>
                You haven&apos;t adopted any mustangs yet!
              </p>
              <p className="text-gray-600 mb-6">
                Visit the Virtual Corral to adopt your first mustang
              </p>
              <button
                onClick={() => (window.location.href = '/virtual-corral')}
                className="px-8 py-3 rounded-full text-white font-semibold"
                style={{ backgroundColor: THEME_COLORS.secondary }}
              >
                Visit Virtual Corral
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-24" style={{ marginTop: '100px' }}>
              {mustangs.map((mustang) => (
                <HorseCard
                  key={mustang.id}
                  mustang={mustang}
                  onEdit={handleEditHorse}
                  onDelete={handleDeleteHorse}
                  onGentlingChange={handleGentlingCheckboxChange}
                  onDailyCareChange={handleDailyCareCheckboxChange}
                  onSpriteChange={handleSpriteChange}
                  onFeed={handleFeed}
                  onWater={handleWater}
                  onGroom={handleGroom}
                  dailyCareCategories={dailyCareCategories}
                  trainingCategories={trainingCategories}
                  isAdmin={user?.is_admin}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Horse Dialog */}
      <AddEditHorseDialog
        open={showAddHorseDialog}
        horse={editingHorse}
        onClose={() => {
          setShowAddHorseDialog(false);
          setEditingHorse(null);
        }}
        onSave={handleSaveHorse}
        api={api}
      />
    </div>
  );
};

export default TheHerd;
